import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { UploadCloud, FileCheck2 } from "lucide-react";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.tiff,.tif,.docx,.xlsx";
const MAX = 100 * 1024 * 1024;

export default function UploadCenter() {
  const [folders, setFolders] = useState([]);
  const [folderId, setFolderId] = useState("");
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/folders");
      setFolders(data);
      if (data[0]) setFolderId(data[0].id);
    })();
  }, []);

  const submit = async (file) => {
    setErr("");
    if (!file) return;
    if (file.size > MAX) {
      setErr("File exceeds 100MB limit.");
      return;
    }
    if (!folderId) {
      setErr("Pick a folder first.");
      return;
    }
    const folder = folders.find((f) => f.id === folderId);
    const fd = new FormData();
    fd.append("team_id", folder.team_id);
    fd.append("folder_id", folderId);
    fd.append("file", file);
    setBusy(true);
    setProgress(0);
    try {
      const { data } = await api.post("/documents/upload", fd, {
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      setRecent((r) => [{ ...data, _folder: folder.name }, ...r].slice(0, 8));
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail));
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) submit(f);
  };

  return (
    <AppShell title="Upload Center">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 ds-card p-5">
          <div className="ds-overline">Upload</div>
          <h3 className="font-display text-lg font-semibold tracking-tight mb-4">
            Add a document
          </h3>

          <label className="ds-label">Destination folder</label>
          <select
            className="ds-input mb-4"
            value={folderId}
            onChange={(e) => setFolderId(e.target.value)}
            data-testid="upload-folder-select"
          >
            {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>

          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="border-2 border-dashed border-[#D1D1D6] p-10 text-center hover:bg-[#F7F7F8] transition-colors"
            data-testid="upload-dropzone"
          >
            <UploadCloud className="mx-auto mb-2" size={28} />
            <div className="text-sm font-semibold">Drag a file here or click below</div>
            <div className="text-[12px] text-[#8A8A93] mt-1">
              PDF, JPG, PNG, TIFF, DOCX, XLSX. Max 100 MB.
            </div>
            <label className="ds-btn-primary inline-block mt-4 cursor-pointer">
              Choose file
              <input
                type="file"
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => submit(e.target.files?.[0])}
                data-testid="upload-file-input"
              />
            </label>
          </div>

          {busy && (
            <div className="mt-4">
              <div className="ds-overline mb-1">Uploading {progress}%</div>
              <div className="h-1.5 bg-[#E5E5E9]">
                <div className="h-1.5 bg-[#0A0A0A]" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}
          {err && <div className="text-[12px] text-[#B42318] mt-3" data-testid="upload-error">{err}</div>}
        </div>

        <div className="ds-card p-5">
          <div className="ds-overline">Just uploaded</div>
          <h3 className="font-display text-lg font-semibold tracking-tight mb-3">In flight</h3>
          {recent.length === 0 ? (
            <div className="text-[12px] text-[#8A8A93]">Nothing yet.</div>
          ) : (
            <ul className="space-y-2">
              {recent.map((d) => (
                <li key={d.id} className="border border-[#E5E5E9] p-3" data-testid={`uploaded-${d.id}`}>
                  <div className="flex items-center gap-2 text-[12px] font-mono truncate">
                    <FileCheck2 size={13} /> {d.filename}
                  </div>
                  <div className="ds-overline mt-1" style={{ fontSize: 10 }}>
                    {d._folder} · OCR queued
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AppShell>
  );
}
